import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Popover } from '@amzn/storm-ui';
import { selectHeaderState, selectIsBackUpNav } from '../../store/slice';
import { RootState } from 'src/app/store';
import { IA2AccountDropdown } from './IA2AccountDropdown';
import { AccountInfo } from './AjaxFetchAccountInfo';
import { AccountDropdownWrapper } from './styles';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { IA2AccountDropdownTrigger } from './IA2AccountDropdownTrigger';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { publishMountToInteractive } from 'src/commons/utils/mountToInteractive';

interface AccountDropdownProps {
  /*
   * When adding hasConsoleBanner (https://sim.amazon.com/issues/CCX-15314)
   * adjust max height of AccountDropdownWrapper
   */
  currentAccountInfo: AccountInfo;
  isBackUpNav: boolean;
}

export const AccountDropdown: React.VFC<AccountDropdownProps> = ({
  currentAccountInfo,
  isBackUpNav,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    accountCounter,
    loadingStatus,
    nextToken = null,
  } = useAccountDropdownContext();
  const isTrimT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM,
  );
  const isTrimT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2,
  );
  const isTrimDialedUp = isTrimT1Enabled || isTrimT2Enabled;
  const mountedAt = useRef<number>();
  const hasPublishedInteractiveLatency = useRef(false);
  if (mountedAt.current === undefined) {
    mountedAt.current = performance.now();
  }

  useEffect(() => {
    const isReady = loadingStatus === 'success' && accountCounter !== 0;
    const isError = loadingStatus === 'error';
    if ((!isReady && !isError) || hasPublishedInteractiveLatency.current) {
      return;
    }

    hasPublishedInteractiveLatency.current = true;
    const latencyMs = Math.round(performance.now() - mountedAt.current!);

    // Only a clean success carries a page-load latency. On a success the cursor
    // decides whale, and only under treatment, since control has no cursor and so
    // is always non-whale.
    publishMountToInteractive(
      AccountSwitcherPublisher,
      isError
        ? { failed: true, latencyMs }
        : {
            failed: false,
            latencyMs,
            isWhale: isTrimDialedUp && nextToken !== null,
          },
    );
  }, [accountCounter, loadingStatus, nextToken, isTrimDialedUp]);

  const currentMarketplaceId = currentAccountInfo.currentMarketplaceId;
  const advertisingAccountsDataTaktId =
    currentMarketplaceId === currentAccountInfo?.currentMarketplaceId
      ? 'aac-account-links-same-mp'
      : `aac-account-links-different-mp-${currentMarketplaceId}`;

  const handleToggle = () => {
    if (isOpen) {
      AccountSwitcherPublisher.publishCounterMonitor('Close', 1);
    } else {
      AccountSwitcherPublisher.publishCounterMonitor('Open', 1);
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => setIsOpen(false);

  return (
    <Popover
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <IA2AccountDropdownTrigger
          isOpen={isOpen}
          handleToggle={handleToggle}
        />
      }
      position="bottom"
      align="start"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={0}
      padding="none"
      data-ccx-e2e-id="aac-account-dropdown-wrapper"
    >
      {!isBackUpNav && (
        <AccountDropdownWrapper>
          <IA2AccountDropdown
            advertisingAccountsDataTaktId={advertisingAccountsDataTaktId}
          />
        </AccountDropdownWrapper>
      )}
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => {
  const headerState = selectHeaderState(state);
  const isBackUpNav = selectIsBackUpNav(state);
  return {
    currentAccountInfo: headerState.currentAccountInfo as AccountInfo,
    isBackUpNav: isBackUpNav,
  };
};

export default connect(mapStateToProps)(AccountDropdown);
